import { useCallback, useMemo } from 'react';

export type DateView = 'Day' | 'Month' | 'Quarter' | 'Year';

/** Clamp a calendar year into yearRange so a year Select never holds a value outside its own options. */
export function clampYearToRange(year: number, yearRange?: { start: number; end: number }): number {
  return yearRange ? Math.min(Math.max(year, yearRange.start), yearRange.end) : year;
}

/**
 * Converts a Date to ISO string. When useLocalTimezone is true, preserves the
 * local timezone offset (e.g. "2026-04-09T04:00:00.000-05:00") instead of UTC (e.g. "2026-04-09T09:00:00.000Z").
 */
export function dateToISOString(date: Date, useLocalTimezone: boolean): string {
  if (!useLocalTimezone) {
    return date.toISOString();
  }
  const offset = -date.getTimezoneOffset();
  const offsetHours = Math.floor(Math.abs(offset) / 60);
  const offsetMinutes = Math.abs(offset) % 60;
  const offsetSign = offset >= 0 ? '+' : '-';
  const offsetStr = `${offsetSign}${String(offsetHours).padStart(2, '0')}:${String(offsetMinutes).padStart(2, '0')}`;

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  const ms = String(date.getMilliseconds()).padStart(3, '0');

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${ms}${offsetStr}`;
}

export type SingleDatePickerValue = {
  date: string; // ISO format: 2026-01-06T00:00:00.000
  view: DateView;
};

export type RangeDatePickerValue = {
  startDate: string;
  endDate: string;
  view: DateView;
};

export const isSingleDatePickerValue = (value: DatePickerValue): value is SingleDatePickerValue => {
  return typeof (value as SingleDatePickerValue).date !== 'undefined';
};

export type DatePickerValue = SingleDatePickerValue | RangeDatePickerValue | null;

// Helper to get last day of month
export function getLastDayOfMonth(year: number, month: number): Date {
  return new Date(year, month, 0);
}

// Helper to get first day of month
export function getFirstDayOfMonth(year: number, month: number): Date {
  return new Date(year, month - 1, 1);
}

// Helper to get last day of quarter
export function getLastDayOfQuarter(year: number, quarter: number): Date {
  const month = quarter * 3; // 3, 6, 9, or 12
  return getLastDayOfMonth(year, month);
}

// Helper to get first day of quarter
export function getFirstDayOfQuarter(year: number, quarter: number): Date {
  const month = (quarter - 1) * 3 + 1; // 1, 4, 7, or 10
  return getFirstDayOfMonth(year, month);
}

// Helper to get last day of year
export function getLastDayOfYear(year: number): Date {
  return new Date(year, 11, 31);
}

// Helper to get first day of year
export function getFirstDayOfYear(year: number): Date {
  return new Date(year, 0, 1);
}

// Format display text for selected date/range
export function formatDisplayText(value: DatePickerValue | null): string {
  if (!value) return '';

  if ('date' in value) {
    // Single date
    const date = new Date(value.date);
    switch (value.view) {
      case 'Day':
        return date.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' });
      case 'Month':
        return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      case 'Quarter': {
        const quarter = Math.floor(date.getMonth() / 3) + 1;
        return `Q${quarter} ${date.getFullYear()}`;
      }
      case 'Year':
        return String(date.getFullYear());
    }
  } else {
    // Range
    const startDate = new Date(value.startDate);
    const endDate = new Date(value.endDate);

    switch (value.view) {
      case 'Day':
        return `${startDate.toLocaleDateString('en-US', {
          month: '2-digit',
          day: '2-digit',
          year: 'numeric',
        })} - ${endDate.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })}`;
      case 'Month':
        return `${startDate.toLocaleDateString('en-US', {
          month: 'short',
          year: 'numeric',
        })} - ${endDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}`;
      case 'Quarter': {
        const startQuarter = Math.floor(startDate.getMonth() / 3) + 1;
        const endQuarter = Math.floor(endDate.getMonth() / 3) + 1;
        return `Q${startQuarter} ${startDate.getFullYear()} - Q${endQuarter} ${endDate.getFullYear()}`;
      }
      case 'Year':
        return `${startDate.getFullYear()} - ${endDate.getFullYear()}`;
    }
  }
}

type DateConverter<T> = {
  toDate: (item: T) => Date;
  compare: (date1: Date, date2: Date) => boolean;
};

// Shared hook for range calculations
export function useRangeState<T>(
  value: DatePickerValue | null,
  rangeStart: Date | null,
  previewRange: { start: Date; end: Date } | null,
  converter: DateConverter<T>,
) {
  const isSelected = useCallback(
    (item: T) => {
      const date = converter.toDate(item);

      // If we have a rangeStart (first click in range), show it as selected
      if (rangeStart && converter.compare(date, rangeStart)) {
        return true;
      }

      if (!value) return false;

      if ('date' in value) {
        const valueDate = new Date(value.date);
        return converter.compare(date, valueDate);
      } else {
        const startDate = new Date(value.startDate);
        const endDate = new Date(value.endDate);
        return date >= startDate && date <= endDate;
      }
    },
    [value, rangeStart, converter],
  );

  const isInRange = useCallback(
    (item: T) => {
      const date = converter.toDate(item);

      // Check preview range first (when hovering during range selection)
      if (previewRange) {
        const { start, end } = previewRange;
        if (date > start && date <= end) {
          return true;
        }
      }

      // Check actual selected range (include end date, exclude start date)
      if (!value || 'date' in value) return false;
      const startDate = new Date(value.startDate);
      const endDate = new Date(value.endDate);
      return date > startDate && date <= endDate;
    },
    [value, previewRange, converter],
  );

  return { isSelected, isInRange };
}

// Shared preview range calculation
export function usePreviewRange<T>(
  rangeStart: Date | null,
  hoveredItem: T | null,
  getEndDate: (item: T) => Date,
  getStartDate: (rangeStart: Date) => Date,
) {
  return useMemo(() => {
    if (!rangeStart || !hoveredItem) return null;
    const startDate = getStartDate(rangeStart);
    const endDate = getEndDate(hoveredItem);

    // Don't show preview if hovered date is before rangeStart (would reset range)
    if (endDate < startDate) {
      return null;
    }
    return { start: startDate, end: endDate };
  }, [rangeStart, hoveredItem, getEndDate, getStartDate]);
}

/**
 * Content key for a DatePickerValue. Effects that sync internal state from the
 * value must key on this, not the object reference — consumers often rebuild
 * the value object every render, and a reference-only change would reset an
 * open calendar (snapping a browsed month back mid-interaction).
 */
export function datePickerValueKey(value: DatePickerValue): string {
  if (!value) return '';
  return 'date' in value ? `${value.date}|${value.view}` : `${value.startDate}|${value.endDate}|${value.view}`;
}

// Utils for converting dates to ISO strings for consumers
/** Convert ISO/date string to DatePickerValue (mirrors AdvancedFilterLine date handling). */
export function stringToDatePickerValue(s: string | undefined | null): DatePickerValue {
  if (s == null || typeof s !== 'string') return null;
  let pickerValue: DatePickerValue = null;
  const str = String(s).trim();
  if (str) {
    try {
      // Date-only strings parse as local dates; new Date() would read them as UTC midnight (previous day west of UTC).
      const dateOnly = str.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      const date = dateOnly
        ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
        : new Date(str);
      if (!Number.isNaN(date.getTime())) {
        pickerValue = { date: date.toISOString(), view: 'Day' };
      }
    } catch {
      // Invalid date string, keep as null
    }
  }
  return pickerValue;
}

/** Extract date string from DatePickerValue (mirrors AdvancedFilterLine onChange). */
export function datePickerValueToString(v: DatePickerValue): string {
  if (!v || !('date' in v) || typeof v.date !== 'string') return '';
  return v.date;
}

/**
 * The picked calendar date as 'YYYY-MM-DD' from the viewer's local components
 * (the picker stores local midnight as an instant) — the date the user saw,
 * independent of timezone.
 */
export function datePickerValueToDateString(v: DatePickerValue): string {
  if (!v || !('date' in v) || typeof v.date !== 'string') return '';
  const date = new Date(v.date);
  if (Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}
